import {
  Box,
  Button,
  Heading,
  Icon,
  Image,
  List,
  Stack,
  Text,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { LuArrowRight, LuCheck } from 'react-icons/lu'

import type { ProfileConfig } from '@/components/views/accountManagement/registerWizard/steps/congratulations/profiles/profilesConfig'

import { PROFILE_GRADIENT } from './profileGradients'

interface ProfileIntroScreenProps {
  profile: ProfileConfig
  onStart: () => void
  /** Optional: launching the sub-flow may take a moment. */
  loading?: boolean
}

/**
 * Intermediate screen explaining a chosen profile before its sub-flow starts.
 * Full-width hero layout consistent with the non-closeable dialog (no diptych).
 */
export const ProfileIntroScreen = ({
  profile,
  onStart,
  loading,
}: ProfileIntroScreenProps) => {
  const { t } = useTranslation()

  const features = t(`${profile.i18nKey}.features`, {
    returnObjects: true,
  }) as string[]

  return (
    <Stack gap="0" w="full" maxW="3xl" mx="auto">
      {/* Bounded hero so the heading never overflows on mobile. */}
      <Box
        position="relative"
        maxH="40dvh"
        minH={{ base: '160px', md: '220px' }}
        rounded="2xl"
        overflow="hidden"
        bgImage={
          profile.image ? undefined : PROFILE_GRADIENT[profile.colorPalette]
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
        p="6"
      >
        {profile.image && (
          <Image
            src={profile.image}
            alt=""
            position="absolute"
            inset="0"
            w="full"
            h="full"
            objectFit="cover"
          />
        )}
        <Icon
          as={profile.icon}
          boxSize={{ base: '14', md: '20' }}
          color="white"
          position="relative"
          filter="drop-shadow(0 2px 8px rgba(0,0,0,0.3))"
        />
      </Box>

      <Stack gap="6" pt="8" px={{ base: '2', md: '0' }}>
        <Stack gap="2">
          <Heading size={{ base: '2xl', md: '4xl' }}>
            {t(`${profile.i18nKey}.introTitle`)}
          </Heading>
          <Text color="fg.muted" textStyle={{ base: 'md', md: 'lg' }}>
            {t(`${profile.i18nKey}.introBaseline`)}
          </Text>
        </Stack>

        <Stack gap="3">
          <Text fontWeight="semibold">
            {t('register.wizard.profiles.featuresLabel')}
          </Text>
          <List.Root variant="plain" gap="3">
            {features.map((feature) => (
              <List.Item
                key={feature}
                display="flex"
                alignItems="center"
                gap="2"
              >
                <List.Indicator asChild color={`${profile.colorPalette}.solid`}>
                  <LuCheck />
                </List.Indicator>
                {feature}
              </List.Item>
            ))}
          </List.Root>
        </Stack>

        <Text fontWeight="medium" color="fg.muted">
          {t('register.wizard.profiles.stepsLabel', {
            count: profile.stepCount,
          })}
        </Text>

        <Button
          size="xl"
          rounded="full"
          colorPalette={profile.colorPalette}
          onClick={onStart}
          loading={loading}
          alignSelf={{ base: 'stretch', sm: 'flex-start' }}
        >
          {t(`${profile.i18nKey}.cta`)}
          <LuArrowRight />
        </Button>
      </Stack>
    </Stack>
  )
}
